import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Shield,
  Phone,
  Building,
  FileText,
  Loader2,
  CheckCircle,
  User
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;

  const [formData, setFormData] = useState({
    phone: user?.phone || "",
    company_name: user?.company_name || "Shree Finserv",
    irdai_license: user?.irdai_license || ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect if no user data
  if (!user) {
    navigate('/advisor/login');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || "Failed to save profile");
        return;
      }

      // Navigate to dashboard with updated user
      navigate('/advisor/dashboard', { state: { user: data } });

    } catch (err) {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/advisor/dashboard', { state: { user } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C] mb-4">
            {user.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="h-20 w-20 rounded-full object-cover"
              />
            ) : (
              <User className="h-10 w-10 text-[#0D1A3A]" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-[#1B2A4A]">Welcome, {user.name}!</h1>
          <p className="text-[#3A4557] mt-1">Complete your profile to get started</p>
        </div>

        {/* Setup Card */}
        <Card className="bg-white/50 border-[#C9D4E6] backdrop-blur-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Advisor Profile Setup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              {/* Email (read-only) */}
              <div>
                <Label className="text-[#2E3B57]">Email</Label>
                <div className="mt-1.5 p-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#3A4557] flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {user.email}
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <Label className="text-[#2E3B57]">Phone Number</Label>
                <div className="relative mt-1.5">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A4557]" />
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    className="pl-10 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <Label className="text-[#2E3B57]">Company / Firm Name</Label>
                <div className="relative mt-1.5">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A4557]" />
                  <Input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    placeholder="Your company name"
                    className="pl-10 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                    data-testid="company-input"
                  />
                </div>
              </div>

              {/* IRDAI License */}
              <div>
                <Label className="text-[#2E3B57]">IRDAI License Number (Optional)</Label>
                <div className="relative mt-1.5">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A4557]" />
                  <Input
                    type="text"
                    value={formData.irdai_license}
                    onChange={(e) => setFormData({...formData, irdai_license: e.target.value})}
                    placeholder="License number"
                    className="pl-10 bg-white/70 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                    data-testid="license-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSkip}
                  className="flex-1 border-[#C9D4E6] text-[#2E3B57] hover:bg-[#EDF1F8]"
                  data-testid="skip-btn"
                >
                  Skip for Now
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold"
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <>Save & Continue</>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-[#3A4557] text-sm mt-6">
          Prashant K Uunadkat • Beshak Verified Advisor
        </p>
      </div>
    </div>
  );
}
